import React, { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import RegionalService from '../services/RegionalService';
import UsuarioService from '../services/UsuarioService';
import { useAuth } from '../contexts/AuthContext';
import { isGeneralAdmin } from '../utils/adminMenu';
import UserAccountMenu from './common/UserAccountMenu';
import './SelectRegional.css';

const getRegionalName = (regional) => regional?.nombre || regional?.name || regional?.descripcion || 'Regional';

const SelectRegional = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { updateToken, user } = useAuth();

  const regionales = useMemo(() => RegionalService.getRegionales() || [], []);
  const activeRegional = useMemo(() => RegionalService.getActiveRegional(), []);
  const redirectPath = location.state?.from?.pathname || '/dashboard';
  const canOpenMonitor = isGeneralAdmin(user);

  useEffect(() => {
    if (activeRegional) {
      navigate(redirectPath, { replace: true });
      return;
    }

    if (!regionales || regionales.length === 0) {
      navigate('/dashboard', { replace: true });
      return;
    }

    if (regionales.length === 1) {
      const regional = regionales[0];
      RegionalService.setActiveRegional(regional);
      const regionalId = regional?.id || regional?.Id;
      if (regionalId) {
        UsuarioService.selectRegional(regionalId)
          .then((res) => {
            const token = res.token || res.Token;
            if (token) {
              updateToken(token);
            }
            navigate(redirectPath, { replace: true });
          })
          .catch(() => navigate(redirectPath, { replace: true }));
      } else {
        navigate(redirectPath, { replace: true });
      }
    }
  }, [activeRegional, regionales, navigate, redirectPath, updateToken]);

  const handleSelect = (regional) => {
    const regionalId = regional?.id || regional?.Id;
    RegionalService.setActiveRegional(regional);
    if (!regionalId) {
      navigate(redirectPath, { replace: true });
      return;
    }

    UsuarioService.selectRegional(regionalId)
      .then((res) => {
        const token = res.token || res.Token;
        if (token) {
          updateToken(token);
        }
        navigate(redirectPath, { replace: true });
      })
      .catch(() => {
        navigate(redirectPath, { replace: true });
      });
  };

  if (!regionales || regionales.length <= 1 || activeRegional) {
    return null;
  }

  return (
    <div className="select-regional-page">
      <div className="select-regional-hero">
        <div className="hero-header">
          <div className="hero-content">
            <p className="eyebrow">Configurar entorno</p>
            <h1>Elegí tu regional</h1>
            <p className="subtitle">
              Selecciona el regional con el que querés trabajar. Podrás cambiarlo más adelante desde tu perfil.
            </p>
          </div>
          <UserAccountMenu />
          {canOpenMonitor && (
            <button
              type="button"
              className="select-regional-monitor"
              onClick={() => navigate('/admin/monitor')}
              title="Monitor operativo"
            >
              <i className="fas fa-chart-line" />
              <span>Monitor</span>
            </button>
          )}
        </div>
      </div>

      <div className="select-regional-card">
        <div className="card-header">
          <div>
            <p className="card-kicker">Regionales disponibles</p>
            <h2>Selecciona una opción</h2>
            <p className="card-subtitle">Mostrando {regionales.length} regionales asociados a tu cuenta.</p>
          </div>
        </div>

        <div className="regional-grid">
          {regionales.map((regional) => (
            <button
              key={regional.id || regional.Id || getRegionalName(regional)}
              className="regional-card"
              type="button"
              onClick={() => handleSelect(regional)}
            >
              <div className="regional-icon">
                <i className="fas fa-map-marker-alt" />
              </div>
              <div className="regional-info">
                <h3>{getRegionalName(regional)}</h3>
                {regional.descripcion && <p className="regional-desc">{regional.descripcion}</p>}
                {regional.detalle && <p className="regional-desc">{regional.detalle}</p>}
              </div>
              <div className="regional-action">
                <span>Elegir</span>
                <i className="fas fa-arrow-right" />
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SelectRegional;
